// ref: https://github.com/mapbox/mapbox-gl-draw/blob/main/src/modes/direct_select.js
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { center, circle, distance, point } from '@turf/turf';

const DrawDirectSelect = MapboxDraw.modes.direct_select;

export const DirectSelect = {
  ...DrawDirectSelect,

  dragVertex: function (state, e, delta) {
    if (this.isRectangle(state)) return this.dragRectangle(state, e, delta);
    if (this.isCircle(state)) return this.dragCircle(state, e, delta);
    return DrawDirectSelect.dragVertex.call(this, state, e, delta);
  }
};

DirectSelect.isRectangle = function (state) {
  const coords = state.feature.coordinates[0];

  if (state.feature.type !== 'Polygon' || coords.length !== 4) {
    return false;
  }

  // Function to check if angle at point B (between points A and C) is ~90°
  const isRightAngle = (a, b, c) => {
    const ab = [b[0] - a[0], b[1] - a[1]];
    const cb = [b[0] - c[0], b[1] - c[1]];
    const dot = ab[0] * cb[0] + ab[1] * cb[1];
    return Math.abs(dot) < 1e-6;
  };

  for (let i = 0; i < 4; i++) {
    const a = coords[i];
    const b = coords[(i + 1) % 4];
    const c = coords[(i + 2) % 4];
    if (!isRightAngle(a, b, c)) return false;
  }

  return true;
};

DirectSelect.isCircle = function (state) {
  return (
    state.feature.type === 'Polygon' &&
    state.feature.coordinates[0].length >= 64
  );
};

DirectSelect.dragRectangle = function (state, _, delta) {
  const coords = state.feature.coordinates[0];
  const coordPaths = state.selectedCoordPaths;

  if (coordPaths.length !== 1) return; // Only support single vertex dragging for simplicity

  const selectedIndex = parseInt(coordPaths[0].split('.').pop(), 10);
  const [lng, lat] = coords[selectedIndex];

  const newLng = lng + delta.lng;
  const newLat = lat + delta.lat;

  // Determine opposite, adjacent indices
  const oppositeIndex = (selectedIndex + 2) % 4;
  const adjacent1Index = (selectedIndex + 1) % 4;
  const adjacent2Index = (selectedIndex + 3) % 4;

  const [oppLng, oppLat] = coords[oppositeIndex];

  const newCoords = [];

  // Maintain rectangle by recomputing all four corners
  newCoords[selectedIndex] = [newLng, newLat];
  newCoords[oppositeIndex] = [oppLng, oppLat];
  newCoords[adjacent1Index] = [newLng, oppLat]; // shares newLng with selected, oppLat
  newCoords[adjacent2Index] = [oppLng, newLat]; // shares newLat with selected, oppLng

  // Update all coordinates
  for (let i = 0; i < 4; i++) {
    state.feature.updateCoordinate(`0.${i}`, newCoords[i][0], newCoords[i][1]);
  }
};

DirectSelect.dragCircle = function (state, _, delta) {
  const coords = state.feature.coordinates[0];
  const coordPaths = state.selectedCoordPaths;

  if (coordPaths.length !== 1) return;

  const selectedIndex = parseInt(coordPaths[0].split('.').pop(), 10);

  // Calculate the absolute center of the polygon
  const circleCenter = center(state.feature);

  // Apply delta to selected point
  const [lng, lat] = coords[selectedIndex];
  const newLng = lng + delta.lng;
  const newLat = lat + delta.lat;

  // Compute new radius using Turf.js distance function
  const radius = distance(
    point([
      circleCenter.geometry.coordinates[0],
      circleCenter.geometry.coordinates[1]
    ]),
    point([newLng, newLat]),
    { units: 'kilometers' }
  );

  // Generate the new circle polygon
  const newCircle = circle(
    [
      circleCenter.geometry.coordinates[0],
      circleCenter.geometry.coordinates[1]
    ],
    radius,
    { steps: 64, units: 'kilometers' }
  );

  // Update the coordinates of the feature
  newCircle.geometry.coordinates[0].forEach((coord, index) => {
    state.feature.updateCoordinate(`0.${index}`, coord[0], coord[1]);
  });
};
